import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Mail, MessageSquare, RefreshCw, Send, X } from 'lucide-react';
import { getLimitOutreachDraft, sendLimitOutreach } from '../../Services/GrowthService';
import { ExhaustionType, LimitOutreachDraft } from '../../Types/GrowthType';

interface OutreachModalProps {
  userId: string | null;
  /** null means "let the server work out which balance ran out". */
  exhaustionType: ExhaustionType | null;
  displayName: string;
  onClose: () => void;
  onSent: (recipient: string) => void;
  onError: (title: string, message: string) => void;
}

/**
 * Editable outreach, with the send itself behind an explicit confirm.
 *
 * <p>The server returns a pre-filled draft rather than the UI writing copy itself, so the numbers
 * in the message ("you have used all 10 of your invite credits") come from one place and cannot
 * drift from what the admin sees on the row. Both channels default on because the user asked for
 * both, and an admin can drop either one - that is a deliberate act with a visible toggle, not a
 * hidden default.
 */
const OutreachModal = ({
  userId,
  exhaustionType,
  displayName,
  onClose,
  onSent,
  onError,
}: OutreachModalProps) => {
  const [draft, setDraft] = useState<LimitOutreachDraft | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendNotification, setSendNotification] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDraft = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await getLimitOutreachDraft(userId, exhaustionType ?? undefined);
      if (response.success && response.data) {
        setDraft(response.data);
        setSubject(response.data.subject ?? '');
        setBody(response.data.body ?? '');
      } else {
        onError('Could not load draft', response.message || 'Please try again.');
        onClose();
      }
    } catch {
      onError('Could not load draft', 'Failed to build the message. Please try again.');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, exhaustionType, onClose, onError]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  // Both channels off means a confirmed send that goes nowhere; treat that as a mistake worth
  // catching before the confirmation step rather than after.
  const nothingSelected = !sendEmail && !sendNotification;

  const handleConfirmSend = async () => {
    if (!userId) return;
    if (nothingSelected) {
      onError('Nothing selected', 'Choose email, in-app notification, or both.');
      setConfirming(false);
      return;
    }

    setSubmitting(true);
    try {
      const response = await sendLimitOutreach({
        userId,
        exhaustionType: exhaustionType ?? undefined,
        subject,
        body,
        sendEmail,
        sendNotification,
      });

      if (response.success) {
        onSent(displayName);
        onClose();
      } else {
        onError('Send failed', response.message || 'Please try again.');
        setConfirming(false);
      }
    } catch {
      onError('Send failed', 'Failed to send the message. Please try again.');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const labelClass =
    'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';

  return (
    <AnimatePresence>
      {userId && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !submitting && !confirming && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-100">Contact {displayName}</h2>
                <p className="text-xs font-semibold text-slate-400">
                  Edit the copy, then confirm. Nothing is sent until you confirm.
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {loading ? (
              <div className="flex flex-1 items-center justify-center gap-2 py-16 text-sm font-semibold text-slate-400">
                <RefreshCw className="h-5 w-5 animate-spin text-cyan-500" />
                Building message...
              </div>
            ) : confirming ? (
              <div className="flex-1 space-y-4">
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                  <p className="text-sm font-black text-amber-300">Send this message?</p>
                  <ul className="mt-3 space-y-1.5 text-xs font-semibold text-slate-300">
                    <li>
                      <span className="text-slate-500">To:</span> {draft?.email || displayName}
                    </li>
                    <li>
                      <span className="text-slate-500">Channels:</span>{' '}
                      {[sendEmail ? 'Email' : null, sendNotification ? 'In-app notification' : null]
                        .filter(Boolean)
                        .join(' + ')}
                    </li>
                    <li>
                      <span className="text-slate-500">Subject:</span> {subject}
                    </li>
                  </ul>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-xs font-semibold whitespace-pre-wrap text-slate-300">
                  {body}
                </div>
              </div>
            ) : (
              <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                <div>
                  <label className={labelClass}>Subject</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
                  />
                </div>
                <div>
                  <label className={labelClass}>Message</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={12}
                    className="w-full resize-y rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
                  />
                  {draft?.availableTokens && draft.availableTokens.length > 0 && (
                    <p className="mt-1 text-[10px] font-semibold text-slate-600">
                      Replaced when sent: {draft.availableTokens.join(', ')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <p className={labelClass}>Send via</p>
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      disabled={!draft?.email}
                      className="h-4 w-4 accent-cyan-500"
                    />
                    <Mail size={15} className="text-cyan-400" />
                    <span className="text-xs font-bold text-slate-200">Email</span>
                    {!draft?.email && (
                      <span className="ml-auto text-[10px] font-semibold text-amber-400">
                        No email on file
                      </span>
                    )}
                  </label>
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={sendNotification}
                      onChange={(e) => setSendNotification(e.target.checked)}
                      className="h-4 w-4 accent-cyan-500"
                    />
                    <MessageSquare size={15} className="text-teal-400" />
                    <span className="text-xs font-bold text-slate-200">In-app notification</span>
                  </label>
                  {nothingSelected && (
                    <p className="text-[10px] font-bold text-rose-400">
                      Pick at least one channel to send anything.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              {confirming ? (
                <>
                  <button
                    onClick={() => setConfirming(false)}
                    disabled={submitting}
                    className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-50"
                  >
                    Back to edit
                  </button>
                  <button
                    onClick={handleConfirmSend}
                    disabled={submitting}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    Confirm &amp; Send
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    disabled={loading || submitting}
                    className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setConfirming(true)}
                    disabled={loading || nothingSelected || !subject.trim() || !body.trim()}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-60"
                  >
                    <Send size={16} />
                    Review &amp; Send
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OutreachModal;